import { MapViewportEventView } from '@mark43/cad-api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SimpleMemoryCache } from '../../common/caches/simple-memory-cache';
import { FeatureLayer, Graphic, WebMap, reactiveUtils } from '../constructors';
import { applyEventIconTheme, createSelectedEventFeatureLayer } from '../helpers/feature-layers';
import { createEventGraphic } from '../helpers/graphics';
import type { MapIconTheme } from '../helpers/map-icon-theme';
import { EsriMapEntityTypeEnum } from '../types';
import { useLayerControl } from './use-feature-layer-control';

type SelectedEventFeatureLayerProps = {
    eventsLayer?: FeatureLayer;
    webMap?: WebMap;
    /** The events as placed on the events layer. */
    events: MapViewportEventView[];
    selectedEventId?: number;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
    /** From `useMapIconTheme`, so the layer appears when the config turns on after the map mounts. */
    mapIconTheme: MapIconTheme;
    didEventChange: (currEvent: MapViewportEventView, prevEvent: MapViewportEventView) => boolean;
};

function raiseToTop(webMap: WebMap, layer: FeatureLayer) {
    const topIndex = webMap.layers.length - 1;
    if (webMap.layers.indexOf(layer) !== topIndex) {
        webMap.reorder(layer, topIndex);
    }
}

/**
 * Draws the selected event a second time, on a layer kept above every other layer. Esri places
 * labels top layer first and keeps the first label that takes a spot, so on the events layer a
 * selected event's label can lose to an event stacked on the same spot, or to any layer above.
 * Only with a map icon theme that `raisesSelectedEvent`.
 */
export const useSelectedEventFeatureLayer = ({
    eventsLayer,
    webMap,
    events,
    selectedEventId,
    selectedMapLedgerEntityType,
    mapIconTheme,
    didEventChange,
}: SelectedEventFeatureLayerProps) => {
    const [selectedEventLayer, setSelectedEventLayer] = useState<FeatureLayer>();
    const layerMapIconThemeRef = useRef(mapIconTheme);
    const entityIdToObjectIdMapCacheRef = useRef(new SimpleMemoryCache<number>());
    const { raisesSelectedEvent } = mapIconTheme;

    useEffect(
        function addSelectedEventLayer() {
            if (!webMap || !raisesSelectedEvent) {
                return;
            }
            const layer = createSelectedEventFeatureLayer(layerMapIconThemeRef.current);
            webMap.layers.add(layer);
            setSelectedEventLayer(layer);
            const { remove } = webMap.layers.on('change', function keepOnTop() {
                raiseToTop(webMap, layer);
            });
            return () => {
                remove();
                webMap.layers.remove(layer);
                setSelectedEventLayer(undefined);
            };
        },
        [webMap, raisesSelectedEvent]
    );

    useEffect(
        function showWhenEventsShow() {
            if (!selectedEventLayer || !eventsLayer) {
                return;
            }
            const handle = reactiveUtils.watch(
                () => eventsLayer.visible,
                function matchEventsLayerVisibility(visible) {
                    selectedEventLayer.visible = visible;
                },
                { initial: true }
            );
            return () => handle.remove();
        },
        [selectedEventLayer, eventsLayer]
    );

    useEffect(
        function matchSelectedEventToMapIconTheme() {
            if (!selectedEventLayer || layerMapIconThemeRef.current === mapIconTheme) {
                return;
            }
            applyEventIconTheme(selectedEventLayer, mapIconTheme);
            layerMapIconThemeRef.current = mapIconTheme;
        },
        [selectedEventLayer, mapIconTheme]
    );

    const selectedEvent =
        selectedMapLedgerEntityType === EsriMapEntityTypeEnum.EVENT
            ? events.find((mapEvent) => mapEvent.entityId === selectedEventId)
            : undefined;
    const selectedEvents = selectedEvent ? [selectedEvent] : [];

    const createSelectedEventGraphics = useCallback(
        (mapViewportEvents: MapViewportEventView[]) => {
            const graphics: Graphic[] = [];
            for (const mapViewportEvent of mapViewportEvents) {
                const graphic = createEventGraphic({
                    objectId: entityIdToObjectIdMapCacheRef.current.get(mapViewportEvent.entityId),
                    mapViewportEvent,
                    selectedEventId,
                    selectedMapLedgerEntityType,
                });
                if (graphic) {
                    graphics.push(graphic);
                }
            }
            return graphics;
        },
        [selectedEventId, selectedMapLedgerEntityType]
    );

    useLayerControl<MapViewportEventView>({
        layer: selectedEventLayer,
        currItems: selectedEvents,
        didItemChange: didEventChange,
        createGraphics: createSelectedEventGraphics,
        entityIdToObjectIdMapCacheRef,
    });

    return { selectedEventLayer };
};
